'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { chartBarStartMs, nextChartBarStartMs } from '@/lib/chart-timeframe'
import {
  type ChartSymbolIdentity,
  qualifyChartSymbol,
} from '@/lib/tradingview/chart-symbol-identity'
import { extractBarAnalytics } from '@/lib/tradingview/datafeeds/candle-analytics'
import type { AnalyticsBar, ChartDatafeed } from '@/lib/tradingview/datafeeds/chart-datafeed'
import {
  type ChartSeriesSubscriptionContext,
  getChartSeriesSubscriptionKey,
} from '@/lib/tradingview/datafeeds/chart-series-subscription-manager'
import { useChartStreams } from '@/service/ws/use-chart-stream'
import type { ChartData } from '@/types/generated/public_api'

const MAX_BACKFILL_BARS = 500
const BACKFILL_TIMEOUT_MS = 15_000

type StreamRuntime = {
  queue: Promise<void>
  lastBarTime: number
  abortController: AbortController | null
  generation: number
}

function chartDataSubscriptionKey(chartData: ChartData): string {
  const identity: ChartSymbolIdentity = { exchange: chartData.exchange, symbol: chartData.symbol }
  return `${qualifyChartSymbol(identity)}|${chartData.timeframe}`
}

function chartDataToBars(chartData: ChartData, timeframeSeconds: number): AnalyticsBar[] {
  const candles = chartData.candles
  if (!candles) return []
  const barsByBucket = new Map<number, AnalyticsBar>()

  candles.ticks.forEach((tick, index) => {
    const rawTime = Number(tick) * 1000
    if (!Number.isFinite(rawTime)) return
    const time = chartBarStartMs(rawTime, timeframeSeconds)
    const analytics = extractBarAnalytics(candles, index)
    const volume = Math.round(candles.volumeBase[index] * 100) / 100
    const existing = barsByBucket.get(time)
    if (!existing) {
      barsByBucket.set(time, {
        time,
        open: candles.open[index],
        high: candles.high[index],
        low: candles.low[index],
        close: candles.close[index],
        volume,
        ...analytics,
      })
      return
    }
    existing.high = Math.max(existing.high, candles.high[index])
    existing.low = Math.min(existing.low, candles.low[index])
    existing.close = candles.close[index]
    existing.volume = volume
    existing.analytics = analytics.analytics
    existing.rawAnalytics = analytics.rawAnalytics
  })

  return [...barsByBucket.values()].sort((left, right) => left.time - right.time)
}

export function useComparisonRealtimeBridge({
  datafeedRef,
  isChartReady,
  enabled = true,
}: {
  datafeedRef: React.RefObject<ChartDatafeed | null>
  isChartReady: boolean
  enabled?: boolean
}): void {
  const [contexts, setContexts] = useState<ChartSeriesSubscriptionContext[]>([])
  const runtimesRef = useRef(new Map<string, StreamRuntime>())
  const previousConnectionStatusRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isChartReady || !datafeedRef.current) return
    const manager = datafeedRef.current.comparisonSeriesManager
    const sync = () => setContexts(manager.getActiveContexts())
    sync()
    return manager.subscribe(sync)
  }, [datafeedRef, isChartReady])

  const contextByKey = useMemo(
    () => new Map(contexts.map((context) => [getChartSeriesSubscriptionKey(context), context])),
    [contexts]
  )
  const payloads = useMemo(
    () =>
      contexts.map(({ identity, timeframeSeconds }) => ({
        symbol: identity.symbol,
        exchange: identity.exchange,
        timeframe: timeframeSeconds,
      })),
    [contexts]
  )

  const getRuntime = useCallback((key: string): StreamRuntime => {
    const existing = runtimesRef.current.get(key)
    if (existing) return existing
    const runtime: StreamRuntime = {
      queue: Promise.resolve(),
      lastBarTime: 0,
      abortController: null,
      generation: 0,
    }
    runtimesRef.current.set(key, runtime)
    return runtime
  }, [])

  const pushBars = useCallback(
    (context: ChartSeriesSubscriptionContext, runtime: StreamRuntime, bars: AnalyticsBar[]) => {
      const datafeed = datafeedRef.current
      if (!datafeed) return
      const qualifiedSymbol = qualifyChartSymbol(context.identity)
      for (const bar of bars) {
        if (bar.time < runtime.lastBarTime) continue
        runtime.lastBarTime = Math.max(runtime.lastBarTime, bar.time)
        datafeed.pushBar(bar, {
          symbol: qualifiedSymbol,
          routingSymbol: qualifiedSymbol,
          timeframeSeconds: context.timeframeSeconds,
        })
      }
    },
    [datafeedRef]
  )

  const backfill = useCallback(
    async (context: ChartSeriesSubscriptionContext, runtime: StreamRuntime, toTimeMs: number) => {
      const datafeed = datafeedRef.current
      if (!datafeed || runtime.lastBarTime <= 0) return
      const timeframeMs = Math.max(1, context.timeframeSeconds) * 1000
      const fromTimeMs = nextChartBarStartMs(runtime.lastBarTime, context.timeframeSeconds)
      const alignedTo = chartBarStartMs(toTimeMs, context.timeframeSeconds)
      if (fromTimeMs > alignedTo) return

      runtime.abortController?.abort()
      const controller = new AbortController()
      runtime.abortController = controller
      const generation = ++runtime.generation
      const timeout = setTimeout(() => controller.abort(), BACKFILL_TIMEOUT_MS)
      try {
        const estimatedBars = Math.ceil((alignedTo - fromTimeMs) / timeframeMs) + 1
        const bars = await datafeed.fetchBars(
          {
            identity: context.identity,
            timeframe: context.timeframeSeconds,
            from: Math.floor(fromTimeMs / 1000),
            to: Math.floor(alignedTo / 1000),
            limit: Math.min(Math.max(estimatedBars, 1), MAX_BACKFILL_BARS),
          },
          controller.signal
        )
        if (controller.signal.aborted || runtime.generation !== generation) return
        pushBars(
          context,
          runtime,
          bars.filter((bar) => bar.time > runtime.lastBarTime && bar.time <= alignedTo)
        )
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn('[useComparisonRealtimeBridge] Failed to backfill bars:', error)
        }
      } finally {
        clearTimeout(timeout)
        if (runtime.abortController === controller) runtime.abortController = null
      }
    },
    [datafeedRef, pushBars]
  )

  const processMessage = useCallback(
    async (chartData: ChartData) => {
      const key = chartDataSubscriptionKey(chartData)
      const context = contextByKey.get(key)
      if (!context) return
      const runtime = getRuntime(key)
      const bars = chartDataToBars(chartData, context.timeframeSeconds)
      if (bars.length === 0) return
      if (
        runtime.lastBarTime > 0 &&
        bars[0].time > nextChartBarStartMs(runtime.lastBarTime, context.timeframeSeconds)
      ) {
        await backfill(context, runtime, bars[0].time - 1)
      }
      pushBars(context, runtime, bars)
    },
    [backfill, contextByKey, getRuntime, pushBars]
  )

  const handleMessage = useCallback(
    (chartData: ChartData) => {
      const key = chartDataSubscriptionKey(chartData)
      if (!contextByKey.has(key)) return
      const runtime = getRuntime(key)
      runtime.queue = runtime.queue
        .then(() => processMessage(chartData))
        .catch((error) =>
          console.warn('[useComparisonRealtimeBridge] Failed to process message:', error)
        )
    },
    [contextByKey, getRuntime, processMessage]
  )

  const { connectionStatus } = useChartStreams({
    connected: isChartReady && enabled && payloads.length > 0,
    payloads,
    onMessage: handleMessage,
  })

  const backfillAll = useCallback(() => {
    contextByKey.forEach((context, key) => {
      const runtime = getRuntime(key)
      runtime.queue = runtime.queue.then(() => backfill(context, runtime, Date.now()))
    })
  }, [backfill, contextByKey, getRuntime])

  useEffect(() => {
    const activeKeys = new Set(contextByKey.keys())
    runtimesRef.current.forEach((runtime, key) => {
      if (activeKeys.has(key)) return
      runtime.abortController?.abort()
      runtime.generation += 1
      runtimesRef.current.delete(key)
    })
  }, [contextByKey])

  useEffect(() => {
    const previous = previousConnectionStatusRef.current
    previousConnectionStatusRef.current = connectionStatus
    if (connectionStatus === 'OPEN' && previous !== null && previous !== 'OPEN') backfillAll()
  }, [backfillAll, connectionStatus])

  useEffect(() => {
    if (!isChartReady || !enabled) return
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') backfillAll()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [backfillAll, enabled, isChartReady])

  useEffect(
    () => () => {
      runtimesRef.current.forEach((runtime) => runtime.abortController?.abort())
      runtimesRef.current.clear()
    },
    []
  )
}
